
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { generateSubmissionIdentifier } from '@/utils/magic-link/helpers';

interface AuthFormProps {
  onAuthSuccess: (session: Session) => void;
  onCancel: () => void;
  initialEmail?: string;
  alternativeEmails?: string[];
}

const AuthForm: React.FC<AuthFormProps> = ({
  onAuthSuccess,
  onCancel,
  initialEmail = '',
  alternativeEmails = []
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const redirectTo = `${window.location.origin}/auth-callback`;

      // Generate a submission identifier
      const submissionIdentifier = generateSubmissionIdentifier();

      // Ensure alternativeEmails is an array of valid emails
      const validAltEmails = Array.isArray(alternativeEmails)
        ? alternativeEmails.filter(e => e && e.includes('@') && e !== email)
        : [];

      // Send the magic link
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${redirectTo}?submission_identifier=${submissionIdentifier}`,
        }
      });

      if (error) throw error;

      // Store email and alternative emails in the database for session recovery
      const { error: sessionError } = await supabase
        .from('magic_link_sessions')
        .upsert({
          email,
          submission_identifier: submissionIdentifier,
          alternative_email: validAltEmails.length > 0 ? validAltEmails : null
        }, { onConflict: 'email,submission_identifier' });

      if (sessionError && sessionError.code !== '23505') { // Ignore unique constraint violations
        console.warn('Non-critical error storing session:', sessionError);
      }

      setEmailSent(true);

      if (onAuthSuccess) {
        onAuthSuccess({ user: { email } } as unknown as Session);
      }

      toast({
        title: "Magic link sent",
        description: "Check your email for the login link. You can open it on any device."
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send magic link",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-md mx-auto">
      {emailSent ? (
        <div className="text-center">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              We've sent a magic link to <strong>{email}</strong>.
              Please check your email and click the link to continue.
              You can open this link on any device.
            </AlertDescription>
          </Alert>
          <div className="mt-6">
            <Button variant="outline" onClick={onCancel} className="mr-2">
              Cancel
            </Button>
            <Button onClick={() => setEmailSent(false)}>
              Try Again
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSendMagicLink}>
          <h2 className="text-xl font-semibold mb-4">Save Your Progress</h2>
          <p className="text-gray-600 mb-6">
            You'll receive a magic link to resume your progress later.
          </p>

          <div className="mb-4">
            <Label htmlFor="email" className="block mb-2">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full"
              required
              disabled={loading}
            />
          </div>

          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Magic Link"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AuthForm;
